<script lang="ts">
  import { pricingPlans, type PricingPlan } from "./pricing_plans"

  export let highlightedPlanId = ""
  export let callToAction = "Start Now"
  export let currentPlanId = ""
  export let center = true

  // Filter plans to only include student plans (all plans are student plans now)
  const studentPlans = pricingPlans.filter((plan) => plan.studentPlan)

  // Simple boolean for annual toggle
  let isAnnual = false

  // Use actual annual pricing from Stripe
  function getDisplayPrice(plan: PricingPlan, isAnnual: boolean): string {
    if (isAnnual && plan.stripe_price_id_annual) {
      const annualPrices: Record<string, string> = {
        free: "$0",
        starter: "$499",
        premium: "$999",
        elite: "$1,499",
      }
      return annualPrices[plan.id] || plan.displayPrice || plan.price
    }
    return plan.displayPrice || plan.price
  }

  function getPriceId(plan: PricingPlan, isAnnual: boolean): string | null {
    return isAnnual
      ? plan.stripe_price_id_annual || plan.stripe_price_id
      : plan.stripe_price_id
  }

  // Calculate savings percentage for display
  function getSavingsPercentage(plan: PricingPlan): number {
    const monthlyPrice = parseFloat(
      plan.displayPrice?.replace("$", "") || plan.price.replace("$", ""),
    )
    const annualPrices: Record<string, number> = {
      free: 0,
      starter: 499,
      premium: 999,
      elite: 1499,
    }
    const annualPrice = annualPrices[plan.id]

    if (annualPrice && !isNaN(monthlyPrice) && monthlyPrice > 0) {
      const yearlyEquivalent = monthlyPrice * 12
      return Math.round(
        ((yearlyEquivalent - annualPrice) / yearlyEquivalent) * 100,
      )
    }
    return 0
  }

  // Calculate daily price for marketing
  function getDailyPrice(plan: PricingPlan, isAnnual: boolean): string {
    if (plan.id === "free") return "$0"

    const price = getDisplayPrice(plan, isAnnual)
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""))

    if (isAnnual) {
      const dailyPrice = numericPrice / 365
      return `$${dailyPrice.toFixed(2)}`
    } else {
      // Approximate 30.4 days per month
      const dailyPrice = numericPrice / 30.4
      return `$${dailyPrice.toFixed(2)}`
    }
  }

  // Set pricing to monthly
  function setMonthly() {
    isAnnual = false
  }

  // Set pricing to annual
  function setAnnual() {
    isAnnual = true
  }

  // Get plan description based on tier
  function getPlanDescription(plan: PricingPlan): string {
    const descriptions: Record<string, string> = {
      free: "For the ambitious starter",
      starter: "For the ambitious beginner",
      premium: "For the determined achiever",
      elite: "For the elite performer",
    }
    return descriptions[plan.id] || plan.description
  }
</script>

<div class="flex flex-col items-center mb-8">
  <div class="flex items-center bg-base-200 p-1 rounded-lg">
    <button
      class="px-4 py-2 text-sm rounded-md {!isAnnual
        ? 'bg-primary text-white'
        : 'text-gray-600'}"
      on:click={setMonthly}
    >
      Monthly
    </button>
    <button
      class="px-4 py-2 text-sm rounded-md {isAnnual
        ? 'bg-primary text-white'
        : 'text-gray-600'}"
      on:click={setAnnual}
    >
      Annual (Save up to 37%)
    </button>
  </div>
</div>

<div
  class="flex flex-col lg:flex-row gap-8 {center
    ? 'place-content-center'
    : ''} flex-wrap mx-auto max-w-6xl"
>
  {#each studentPlans as plan}
    <div
      class="flex-none card card-bordered shadow-xl flex-1 min-w-[280px] max-w-[350px] p-8 {plan.id ===
        highlightedPlanId || plan.recommended
        ? 'border-primary border-2 relative'
        : 'border-gray-200'}"
    >
      {#if plan.recommended}
        <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span
            class="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium"
          >
            Most Popular
          </span>
        </div>
      {/if}

      <div class="flex flex-col h-full">
        <div class="text-2xl font-bold mb-1">
          {plan.displayName || plan.name}
        </div>
        <p class="text-sm text-gray-500 mb-6">
          {getPlanDescription(plan)}
        </p>

        <!-- Price section -->
        <div class="mb-2">
          <span class="text-4xl font-bold">
            {getDisplayPrice(plan, isAnnual)}
          </span>
          <span class="text-gray-400">/{isAnnual ? "year" : "month"}</span>

          {#if isAnnual && plan.stripe_price_id_annual && getSavingsPercentage(plan) > 0}
            <div class="mt-1 text-sm text-green-600 font-medium">
              Save {getSavingsPercentage(plan)}% with annual billing
            </div>
          {/if}
        </div>

        <!-- Daily price display -->
        {#if plan.id !== "free"}
          <div class="mb-8 text-sm font-medium text-gray-600">
            Just {getDailyPrice(plan, isAnnual)}/day
          </div>
        {:else}
          <div class="mb-8 text-sm font-medium text-gray-600">&nbsp;</div>
        {/if}

        <!-- Features section -->
        <div class="mt-auto text-sm text-gray-600">
          <ul class="space-y-4">
            {#each plan.features as feature}
              <li class="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-green-500 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{feature}</span>
              </li>
            {/each}
          </ul>
        </div>

        <!-- CTA button -->
        <div class="pt-8 mt-6">
          {#if plan.id === currentPlanId}
            <div
              class="btn btn-outline btn-success no-animation w-full mx-auto cursor-default py-3"
            >
              Current Plan
            </div>
          {:else}
            <a
              href={"/account/subscribe/" +
                (getPriceId(plan, isAnnual) ?? "free_plan")}
              class="btn {plan.recommended
                ? 'btn-primary'
                : 'btn-outline btn-primary'} w-full py-3 text-base font-medium"
            >
              {plan.id === "free" ? "Start Now" : callToAction}
            </a>
          {/if}
        </div>
      </div>
    </div>
  {/each}
</div>
